import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, NativeSelect, ScrollArea } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { ChatbotProps, LoadingState } from "./types";
import { useChatContext } from "./ChatProvider";
import { clearActions, setError, setSelectedModel, updateContext } from "./store/chatSlice";
import {
  ContextPanel,
  MessageList,
  QuestionForm,
  SlowResponseShimmer,
  StatusUpdates,
  TimeoutErrorMessage,
} from "./components";
import { useAgentChat } from "./hooks/useAgentChat";
import { useCancelGeneration } from "./hooks/useCancelGeneration";
import { ApiHelper } from "../api/index";
import { TodoProgress } from "./components/todoProgress";
import { cn } from "../../shared/utils";
import RefetchSession from "./components/refetchSession/RefetchSession";
import { getResponseErrorMessage } from "./utils";

export const Chatbot: FC<ChatbotProps> = ({
  helloMessage,
  context,
  fileUploadProps,
  contextOptions,
  commandHints,
  placeholder,
  autoFocus,
  initialMessage,
  submitInitialMessageOnMount,
  questionFormLeftActions,
  refetchSession,
  chatboxStartPosition = "bottom",
  helloMessageFooter,
  handleSavePrompt,
  showSavePromptButton = false,
}) => {
  const {
    dispatch,
    currentSessionId,
    sessions,
    loadingState,
    error,
    currentActions,
    currentTodos = [],
    requestParams,
    urls,
    models,
    classNames,
    components,
  } = useChatContext();
  const { sendMessage } = useAgentChat();
  const cancelGeneration = useCancelGeneration();

  // Tracks whether the current initial request was cancelled due to client-side timeout.
  // Reset on new loading cycle or when switching sessions so the error doesn't leak across chats.
  const [requestTimedOut, setRequestTimedOut] = useState(false);
  useEffect(() => {
    if (loadingState === LoadingState.LOADING) {
      setRequestTimedOut(false);
    }
  }, [loadingState]);
  useEffect(() => {
    setRequestTimedOut(false);
  }, [currentSessionId]);

  const handleTimeout = useCallback(() => {
    cancelGeneration("client_timeout");
    setRequestTimedOut(true);
  }, [cancelGeneration]);

  // Add refs for scroll management
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const currentSession = currentSessionId ? sessions[currentSessionId] : null;
  // Guard to prevent multiple initial message submissions (fixes infinite loop with V2 verification)
  const initialMessageSubmittedRef = useRef(false);

  // Reset the guard when session changes to a fresh session (no messages),
  // so that subsequent "Analyse the query" clicks can auto-submit again
  useEffect(() => {
    if (currentSessionId && !currentSession?.messages?.length) {
      initialMessageSubmittedRef.current = false;
    }
  }, [currentSessionId, currentSession?.messages?.length]);

  // Add ref to track if we were at bottom before update
  const wasAtBottomRef = useRef(true);

  useEffect(() => {
    // @ts-ignore
    ApiHelper.get = async (
      url: string,
      data: Record<string, unknown>,
      request: RequestInit = {}
    ) => {
      console.log("api helper get", url, data, request);
      const queryParams = new URLSearchParams();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
      const queryString = queryParams.toString();
      const fullUrl = `${urls?.origin || "https://api.getaltimate.com"}${url}${
        queryString ? `?${queryString}` : ""
      }`;
      const response = await fetch(fullUrl, {
        ...requestParams,
        method: "GET",
        headers: {
          ...requestParams?.headers,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        const json = (await response.json()) || {};
        throw new Error(getResponseErrorMessage(json, "Failed to fetch"));
      }
      return response.json();
    };
    // @ts-ignore
    ApiHelper.post = async (
      url: string,
      data: Record<string, unknown>,
      request: RequestInit = {}
    ) => {
      console.log("api helper post", url, data, request);
      const response = await fetch(`${urls?.origin || "https://api.getaltimate.com"}/${url}`, {
        ...requestParams,
        method: request?.method || "POST",
        body: JSON.stringify(data),
        headers: {
          ...requestParams?.headers,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        const json = (await response.json()) || {};
        throw new Error(getResponseErrorMessage(json, "Failed to fetch"));
      }
      return response.json();
    };
  }, [requestParams, urls?.origin]);

  useEffect(() => {
    if (currentSessionId) {
      dispatch(updateContext({ sessionId: currentSessionId, context: context || {} }));
    }
  }, [currentSessionId, dispatch, context]);

  // Reset actions when loading state changes to loaded
  useEffect(() => {
    if (loadingState === LoadingState.LOADED) {
      dispatch(clearActions());
    }
  }, [loadingState, dispatch]);

  const hasMessages = !!currentSession?.messages?.length;
  const shouldShowMessagesContent = hasMessages || loadingState === LoadingState.LOADING || !!error;

  // Guard against showing duplicate StatusUpdates - if the last message already has actions
  // embedded, don't also show them via currentActions (prevents double rendering during streaming)
  const lastMessage = currentSession?.messages?.[currentSession.messages.length - 1];
  const lastMessageHasActions =
    lastMessage?.role === "assistant" && (lastMessage?.actions?.length ?? 0) > 0;

  // Update isScrolledToBottom to be more reliable
  const isScrolledToBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return false;

    const threshold = 50;
    return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  };

  // Add scroll event listener to track scroll position
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      wasAtBottomRef.current = isScrolledToBottom();
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  // Modify scroll effect to use wasAtBottom state
  useEffect(() => {
    // Only scroll if we were at bottom before new content
    if (wasAtBottomRef.current) {
      const container = messagesContainerRef.current;
      if (container) {
        // Use requestAnimationFrame to ensure DOM has updated
        requestAnimationFrame(() => {
          container.scrollTop = container.scrollHeight;
        });
      }
    }
  }, [currentSession?.messages, currentActions]);

  // Add separate effect for loading and error states
  useEffect(() => {
    if (loadingState === LoadingState.LOADING || error) {
      const container = messagesContainerRef.current;
      if (container && wasAtBottomRef.current) {
        requestAnimationFrame(() => {
          container.scrollTop = container.scrollHeight;
        });
      }
    }
  }, [loadingState, error]);

  // Handle model selection
  const handleModelChange = useCallback(
    (model: string) => {
      if (currentSessionId) {
        dispatch(setSelectedModel({ sessionId: currentSessionId, model }));
      }
    },
    [currentSessionId, dispatch]
  );

  const handleSubmitQuestion = useCallback(
    async (message: string) => {
      if (!currentSessionId) return;

      try {
        await sendMessage({ message });
      } catch (e) {
        console.log(e);
      }
    },
    [currentSessionId, sendMessage]
  );

  // Text of the most recent user message, used to re-send on Retry.
  const lastUserMessageContent = useMemo(() => {
    const messages = currentSession?.messages ?? [];
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "user") {
        return messages[i].content;
      }
    }
    return "";
  }, [currentSession?.messages]);

  // Re-send the last user message without appending a duplicate bubble.
  const handleRetry = useCallback(() => {
    if (!currentSessionId || !lastUserMessageContent) return;
    dispatch(setError(null));
    sendMessage({
      message: lastUserMessageContent,
      dontAddUserMessage: true,
    }).catch(e => {
      console.error(e);
    });
  }, [currentSessionId, lastUserMessageContent, sendMessage, dispatch]);

  // Auto-submit initial message on mount if enabled
  useEffect(() => {
    if (
      submitInitialMessageOnMount &&
      initialMessage &&
      currentSessionId &&
      !hasMessages &&
      !initialMessageSubmittedRef.current
    ) {
      initialMessageSubmittedRef.current = true;
      handleSubmitQuestion(initialMessage);
    }
  }, [
    submitInitialMessageOnMount,
    currentSessionId,
    initialMessage,
    hasMessages,
    handleSubmitQuestion,
  ]);

  // Create memoized array combining custom and default actions
  const allActions = useMemo(() => {
    const actions = [];

    // Add custom actions from props
    if (questionFormLeftActions) {
      actions.push(...questionFormLeftActions);
    }

    // Add ContextPanel
    actions.push(
      <ContextPanel fileUploadProps={fileUploadProps} contextOptions={contextOptions} />
    );

    // Add Model selector if available
    if (models && models.length > 0) {
      actions.push(
        <NativeSelect
          aria-label="Model selection"
          options={models}
          value={currentSession?.selectedModel || undefined}
          onChange={handleModelChange}
          placeholder="Select model"
          variant="outline"
          size="sm"
          className="al-w-48"
        />
      );
    }

    return actions;
  }, [
    questionFormLeftActions,
    fileUploadProps,
    contextOptions,
    models,
    currentSession?.selectedModel,
    handleModelChange,
  ]);

  const justificationClass =
    !hasMessages && chatboxStartPosition === "middle"
      ? "al-justify-center"
      : !hasMessages && chatboxStartPosition === "bottom"
        ? "al-justify-end"
        : "";

  return (
    <Card
      className={cn("al-w-full al-h-full al-max-w-5xl al-flex al-flex-col", classNames?.chatbot)}
      role="region"
      aria-label="Chat interface"
      data-session-id={currentSessionId}
    >
      <ScrollArea
        ref={messagesContainerRef}
        className={cn(
          "al-w-full",
          "al-transition-[flex-grow] al-duration-300 ease-in-out",
          // added for fixing horizontal scroll issue due to display:table inside scrollarea
          "[&_[aria-label='scrollable-content']>div]:!al-block",
          {
            "al-p-4": shouldShowMessagesContent,
            "al-flex-grow": hasMessages,
            "al-flex-grow-0": !hasMessages,
          },
          classNames?.chatMessages
        )}
        role="region"
        aria-label="Chat messages"
      >
        <div
          className={cn("al-w-full al-pb-2", classNames?.chatbotScrollingContent)}
          // ref={contentRef}
        >
          <Stack direction="column" gap="5xl">
            {hasMessages && (
              <MessageList
                messages={currentSession?.messages || []}
                handleSavePrompt={handleSavePrompt}
                showSavePromptButton={showSavePromptButton}
              />
            )}
            {loadingState === LoadingState.LOADING && (
              <>
                {currentActions.length > 0 && !lastMessageHasActions && (
                  <div className="al-mt-2">
                    <StatusUpdates
                      actions={currentActions}
                      isComplete={false}
                      aria-live="polite"
                      heading=""
                    />
                  </div>
                )}
                {currentTodos.length > 0 && (
                  <div className="al-mt-2">
                    <TodoProgress todos={currentTodos} />
                  </div>
                )}
                {!currentActions.length && !currentTodos.length ? (
                  <SlowResponseShimmer
                    onTimeout={handleTimeout}
                    isInitialResponse={(currentSession?.messages?.length ?? 0) <= 1}
                  />
                ) : null}
              </>
            )}
            {requestTimedOut && <TimeoutErrorMessage />}
            <RefetchSession
              error={error}
              currentSession={currentSession}
              refetchSession={refetchSession}
              onRetry={handleRetry}
            />
          </Stack>
          {/* Add an empty div as last element for scroll detection */}
          <div ref={lastMessageRef} />
        </div>
      </ScrollArea>
      <div
        className={cn(
          "al-p-4 al-pb-0 al-transition-[flex-grow] al-duration-300 ease-in-out",
          "al-flex al-flex-col",
          {
            "al-flex-grow": !hasMessages,
            "al-flex-grow-0": hasMessages,
          },
          justificationClass,
          classNames?.questionForm
        )}
        role="region"
        aria-label="Message input"
      >
        <Stack direction="column" gap="none">
          {helloMessage && !hasMessages && (
            <div className="al-mb-4" role="status">
              {helloMessage}
            </div>
          )}
          <QuestionForm
            isLoading={loadingState === LoadingState.LOADING}
            allowedFiles={
              fileUploadProps?.enforceAllowedFiles ? fileUploadProps.allowedFiles : undefined
            }
            placeholder={placeholder}
            autoFocus={autoFocus}
            // if submitted on mount, dont set in question form input
            initialMessage={submitInitialMessageOnMount ? undefined : initialMessage}
            contextOptions={contextOptions}
            commandHints={commandHints}
            components={{
              questionFormLeftActions: (
                <Stack align="center">
                  {allActions.map((action, index) => (
                    <div key={index}>{action}</div>
                  ))}
                </Stack>
              ),
              questionFormButtons: components?.questionFormButtons,
            }}
          />
          {helloMessageFooter && !hasMessages && (
            <div className="al-mt-4" role="status">
              {helloMessageFooter}
            </div>
          )}
        </Stack>
      </div>
    </Card>
  );
};
